/**
 * COA Dashboard — Top-level container for Compliance Oversight Activities
 * Wraps the existing COAWorkPlan and adds Risk Register integration.
 * This is the incremental path toward full refactoring of the 2300-line mega component.
 */
import React, { useState, useEffect, useCallback } from 'react';
import COAWorkPlan from './COAWorkPlan';

const API = process.env.REACT_APP_API_URL || '';

export default function COADashboard({ user = null }) {
  const [activeView, setActiveView] = useState('workplan'); // 'workplan' | 'risk-linked'
  const [riskLinkedActivities, setRiskLinkedActivities] = useState([]);
  const [loadingRiskLinks, setLoadingRiskLinks] = useState(false);
  const [dashboardStats, setDashboardStats] = useState(null);

  const loadDashboardStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/coa/dashboard-stats`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setDashboardStats(data.stats);
    } catch (_) {}
  }, []);

  useEffect(() => { loadDashboardStats(); }, [loadDashboardStats]);

  if (activeView === 'workplan') {
    // These bars are passed into COAWorkPlan and rendered inside its content
    // column, so the work plan's dark left sidebar spans the full height with no
    // white strip above it.
    const topBar = (
      <>
        {/* Launch the centralized Audit tool for an internal audit */}
        <div className="bg-white border-b border-slate-200 px-6 py-2 flex items-center justify-end shrink-0">
          <button onClick={() => { window.location.href = '/app?app=audit'; }}
            className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow">
            + Run an audit
          </button>
        </div>
        {/* Quick stats bar */}
        {dashboardStats && (
          <div className="bg-white border-b border-slate-200 px-6 py-3 flex items-center gap-6 text-sm shrink-0">
            <span className="text-slate-400">Activities:</span>
            <Stat label="Draft" value={dashboardStats.draft} color="text-slate-600" />
            <Stat label="In Progress" value={dashboardStats.in_progress} color="text-blue-600" />
            <Stat label="Completed" value={dashboardStats.completed} color="text-green-600" />
            {dashboardStats.from_risk_register > 0 && (
              <Stat label="From Risk Register" value={dashboardStats.from_risk_register} color="text-amber-600" />
            )}
            {dashboardStats.pending_rebuttals > 0 && (
              <Stat label="Pending Rebuttals" value={dashboardStats.pending_rebuttals} color="text-amber-600" />
            )}
          </div>
        )}
      </>
    );
    return <COAWorkPlan user={user} topBar={topBar} />;
  }

  return null;
}

function Stat({ label, value, color }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-slate-400">{label}</span>
      <span className={`font-semibold ${color}`}>{value || 0}</span>
    </div>
  );
}
